import { addMinutes, differenceInMinutes, isAfter, isBefore } from 'date-fns';
import type { Poll, PollDataObject, Program, UploadedFile } from 'models';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AttachmentsInput,
  type AttahcmentsInputUploadFile,
  Col,
  type DatePickerDateType,
  DateTimeInput,
  Form,
  FormActions,
  Modal,
  RichTextInput,
  Row,
  Section,
  SelectInput,
  TextInput,
  Title,
  getDatePickerDateType,
  getDateType,
  message,
} from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { CommunitySelect, useCommunitySelectCreateOption } from 'features/communities/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { useCommunityMentions } from 'features/communities/hooks';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { useProgramMentions } from 'features/programs/hooks';
import { PollQuestions, type QuestionFormValues } from './features';
import { useGetPollInitialValues } from './hooks';

export type PollFormValues = {
  attachments?: { fileList: AttahcmentsInputUploadFile[] };
  community: { label: string; value: string } | undefined;
  description?: string;
  endTime: DatePickerDateType;
  htmlDescription?: string;
  pinned: boolean;
  programsTopicId?: string;
  questions?: QuestionFormValues[];
  startTime: DatePickerDateType;
  title: string;
};

export type PollSubmitData = {
  attachments?: UploadedFile[];
  community_id?: string;
  description?: string;
  html_description?: string;
  program_id?: string;
  programs_topic_id?: string;
  questions: QuestionFormValues[];
} & Omit<PollDataObject, 'attachments' | 'description' | 'html_description' | 'questions'>;

type P = {
  edit?: boolean;
  loading: boolean;
  onBack?: () => void;
  onSubmit: (poll: PollSubmitData) => void;
  poll: Poll;
  program?: Program;
};

const defaultQuestion = () => ({ choices: [{ name: '' }], multiple: true, question: '' });

const PollForm = ({ edit, loading, onBack, onSubmit, poll, program }: P) => {
  const [form] = Form.useForm<PollFormValues>();
  const isProgram = Boolean(program);
  const getCommunityId = useCallback(() => form.getFieldsValue().community?.value, [form]);
  const handleOnRequestCommunityMentions = useCommunityMentions(getCommunityId);
  const handleOnRequestProgramMentions = useProgramMentions(program?.id);

  const onRequestMentions = useCallback(
    async (value: string) =>
      isProgram ? handleOnRequestProgramMentions(value) : handleOnRequestCommunityMentions(value),
    [isProgram, handleOnRequestCommunityMentions, handleOnRequestProgramMentions],
  );

  const initialValues = useGetPollInitialValues({ poll });
  const { t } = useTranslation();
  const createOption = useCommunitySelectCreateOption();

  const onFinish = (data: PollFormValues) => {
    /* eslint-disable @typescript-eslint/naming-convention -- Matches the BE poll payload's field names */
    const cleanedPoll: PollSubmitData = {
      attachments: data.attachments?.fileList.map((file) => file.response),
      community_id: data.community?.value,
      description: data.description,
      end_time: getDateType(data.endTime).toISOString(),
      html_description: data.htmlDescription,
      id: poll.id,
      pinned: data.pinned,
      program_id: program?.id,
      programs_topic: data.programsTopicId ? { id: data.programsTopicId } : undefined,
      programs_topic_id: data.programsTopicId,
      questions: data.questions ?? poll.questions,
      start_time: isProgram ? getDateType(data.startTime).toISOString() : undefined,
      title: data.title,
    };
    /* eslint-enable @typescript-eslint/naming-convention -- Matches the BE poll payload's field names */

    onSubmit(cleanedPoll);
  };

  return (
    <Form form={form} initialValues={initialValues} onFinish={onFinish}>
      <Section paddingBottom={false} paddingTop={false}>
        <Title level={3} marginBottom>
          Poll details
        </Title>
        {program ? (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          <SelectInput<any>
            input={{
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
              getOptionLabel: (option) => option.name,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
              getOptionValue: (option) => option.id,
              // TODO these are not same types! there is any now for this case
              options: program.topics,
              placeholder: t('Please select the module the poll belongs to'),
            }}
            item={{
              'data-test-id': 'select_program-topic',
              label: 'Program module',
              name: 'programsTopicId',
              rules: [
                {
                  required: true,
                },
              ],
            }}
          />
        ) : null}
        {!isProgram && edit ? (
          <Form.Item<PollFormValues>
            data-test-id="select_communities"
            label="Select community"
            name="community"
            rules={[{ required: true }]}
          >
            <CommunitySelect createOption={createOption} />
          </Form.Item>
        ) : null}
        <Row gutter="ms">
          {isProgram ? (
            <Col span={12}>
              <DateTimeInput
                input={{
                  minimum: new Date(),
                  showTime: true,
                }}
                item={{
                  getValueFromEvent(newStartTime: DatePickerDateType): DatePickerDateType {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                    const endTime = form.getFieldValue('endTime') as DatePickerDateType;

                    if (isAfter(getDateType(newStartTime), addMinutes(getDateType(endTime), -60))) {
                      message.warn('End time was changed to 1 hour later according to start time');
                      form.setFieldsValue({
                        endTime: getDatePickerDateType(addMinutes(getDateType(newStartTime), 60)),
                      });
                    }

                    return newStartTime;
                  },
                  label: 'Starts on',
                  name: 'startTime',
                  rules: [
                    {
                      required: true,
                    },
                    () => ({
                      // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                      async validator(_, value: DatePickerDateType) {
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                        if (isProgram && program?.startTime && isBefore(getDateType(value), program.startTime)) {
                          throw new Error(t('Error: Poll start time must be set after Program start time'));
                        }
                      },
                    }),
                  ],
                }}
              />
            </Col>
          ) : null}
          <Col span={12}>
            <DateTimeInput
              input={{
                minimum: new Date(),
                showTime: true,
              }}
              item={{
                getValueFromEvent(newEndTime: DatePickerDateType): DatePickerDateType {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                  const startTime = form.getFieldValue('startTime') as DatePickerDateType;

                  if (
                    isBefore(getDateType(newEndTime), getDateType(startTime)) ||
                    differenceInMinutes(getDateType(newEndTime), getDateType(startTime)) < 60
                  ) {
                    message.warn('End time was changed to 1 hour later according to start time');
                    return getDatePickerDateType(addMinutes(getDateType(startTime), 60));
                  }

                  return newEndTime;
                },
                label: 'Ends on',
                name: 'endTime',
                rules: [
                  {
                    required: true,
                  },
                  () => ({
                    message: t('Error: Poll end time must be set before Program end time'),
                    // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                    async validator(_, value: DatePickerDateType) {
                      if (isProgram && program?.endTime && isAfter(getDateType(value), program.endTime)) {
                        throw new Error(t('Error: Poll end time must be set before Program end time'));
                      }
                    },
                  }),
                ],
              }}
            />
          </Col>
        </Row>
        <TextInput
          item={{
            label: 'Title',
            name: 'title',
            rules: [{ required: true }],
          }}
        />
      </Section>
      {(!poll.id || program?.status === 'draft') && (
        <Section lineBottom={false} paddingBottom={false}>
          <Title level={3} marginBottom>
            Poll content
          </Title>
          <PollQuestions defaultQuestion={defaultQuestion()} name="questions" />
        </Section>
      )}
      {Boolean(edit) || isProgram ? (
        <>
          <Section paddingBottom={false}>
            <Title level={3} marginBottom>
              Attachments
            </Title>
            <AttachmentsInput item={{ name: 'attachments' }} />
          </Section>
          <Section lineBottom={false} paddingBottom={false}>
            <Title level={3} marginBottom>
              Additional details
            </Title>
            <RichTextInput
              html={{
                label: 'Description',
                mentions: onRequestMentions,
                name: 'htmlDescription',
              }}
              text={{ name: 'description' }}
            />
          </Section>
        </>
      ) : null}
      <Modal.HugContent.Footer>
        <FormActions
          left={onBack ? { onClick: onBack } : undefined}
          submit={{ children: edit ? 'Edit Poll' : 'Create Poll', id: 'submit-poll', loading }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default PollForm;
